import { useState } from 'react'
import { Link, NavLink, useNavigate } from 'react-router-dom'
import { useAuthStore } from '../../store'

/**
 * Header principale con navigazione
 * Include logo, menu desktop, menu mobile e area utente
 */
export default function Header() {
  const [menuAperto, setMenuAperto] = useState(false)
  const [userMenuAperto, setUserMenuAperto] = useState(false)
  const { utente, logout } = useAuthStore()
  const navigate = useNavigate()
  const isAuthenticated = utente !== null
  const isAdmin = utente?.ruolo === 'admin'

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `transition-colors duration-200 hover:text-gold ${
      isActive ? 'text-gold font-semibold' : 'text-gray-300'
    }`

  const menuItems = [
    { to: '/', label: 'Home' },
    { to: '/locali', label: 'Locali' },
    { to: '/segnala', label: 'Segnala', highlight: true },
    { to: '/come-funziona', label: 'Come funziona' },
    { to: '/il-club', label: 'Il Club' },
  ]

  const handleLogout = async () => {
    await logout()
    setUserMenuAperto(false)
    navigate('/')
  }

  return (
    <header className="bg-night-light border-b border-gray-800 sticky top-0 z-50">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group">
            <img
              src="/images/logo-conero-garage.webp"
              alt="Conero Garage Approved"
              className="h-12 md:h-16 w-auto"
            />
          </Link>

          {/* Menu Desktop */}
          <nav className="hidden md:flex items-center gap-6">
            {menuItems.map((item) => (
              item.highlight ? (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className="bg-gold text-night px-4 py-2 rounded-lg font-medium hover:bg-gold-light transition-colors"
                >
                  {item.label}
                </NavLink>
              ) : (
                <NavLink key={item.to} to={item.to} className={linkClass}>
                  {item.label}
                </NavLink>
              )
            ))}

            {/* Separatore */}
            <div className="w-px h-6 bg-gray-700" />

            {/* Area utente desktop */}
            {isAuthenticated ? (
              <div className="relative">
                <button
                  onClick={() => setUserMenuAperto(!userMenuAperto)}
                  className="flex items-center gap-2 text-gray-300 hover:text-gold transition-colors"
                >
                  <div className="w-8 h-8 bg-gold/20 rounded-full flex items-center justify-center">
                    <span className="text-gold text-sm font-medium">
                      {utente?.nome.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <span className="text-sm">{utente?.nome}</span>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* Dropdown menu */}
                {userMenuAperto && (
                  <div className="absolute right-0 mt-2 w-48 bg-night-light border border-gray-700 rounded-lg shadow-xl py-2 z-50">
                    <Link
                      to="/profilo"
                      onClick={() => setUserMenuAperto(false)}
                      className="block px-4 py-2 text-gray-300 hover:bg-gray-800 hover:text-gold"
                    >
                      Il mio profilo
                    </Link>
                    {isAdmin && (
                      <Link
                        to="/admin"
                        onClick={() => setUserMenuAperto(false)}
                        className="block px-4 py-2 text-gold hover:bg-gray-800"
                      >
                        Pannello Admin
                      </Link>
                    )}
                    <hr className="my-2 border-gray-700" />
                    <button
                      onClick={handleLogout}
                      className="block w-full text-left px-4 py-2 text-gray-400 hover:bg-gray-800 hover:text-red-400"
                    >
                      Esci
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-4">
                <Link to="/login" className="text-gray-300 hover:text-gold transition-colors text-sm">
                  Accedi
                </Link>
                <Link
                  to="/registrati"
                  className="border border-gold text-gold px-4 py-2 rounded-lg text-sm hover:bg-gold hover:text-night transition-colors"
                >
                  Registrati
                </Link>
              </div>
            )}
          </nav>

          {/* Hamburger Mobile */}
          <button
            className="md:hidden p-2 text-gray-300 hover:text-gold transition-colors"
            onClick={() => setMenuAperto(!menuAperto)}
            aria-label={menuAperto ? 'Chiudi menu' : 'Apri menu'}
            aria-expanded={menuAperto}
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              {menuAperto ? (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              ) : (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              )}
            </svg>
          </button>
        </div>

        {/* Menu Mobile */}
        {menuAperto && (
          <nav className="md:hidden py-4 border-t border-gray-800 animate-fade-in">
            <ul className="flex flex-col gap-4">
              {menuItems.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    className={item.highlight
                      ? "inline-block bg-gold text-night px-4 py-2 rounded-lg font-medium"
                      : linkClass
                    }
                    onClick={() => setMenuAperto(false)}
                  >
                    {item.label}
                  </NavLink>
                </li>
              ))}

              {/* Separatore */}
              <li><hr className="border-gray-700" /></li>

              {/* Area utente mobile */}
              {isAuthenticated ? (
                <>
                  <li>
                    <Link
                      to="/profilo"
                      onClick={() => setMenuAperto(false)}
                      className="text-gray-300 hover:text-gold"
                    >
                      Il mio profilo
                    </Link>
                  </li>
                  {isAdmin && (
                    <li>
                      <Link
                        to="/admin"
                        onClick={() => setMenuAperto(false)}
                        className="text-gold"
                      >
                        Pannello Admin
                      </Link>
                    </li>
                  )}
                  <li>
                    <button
                      onClick={() => { handleLogout(); setMenuAperto(false); }}
                      className="text-gray-400 hover:text-red-400"
                    >
                      Esci
                    </button>
                  </li>
                </>
              ) : (
                <>
                  <li>
                    <Link
                      to="/login"
                      onClick={() => setMenuAperto(false)}
                      className="text-gray-300 hover:text-gold"
                    >
                      Accedi
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/registrati"
                      onClick={() => setMenuAperto(false)}
                      className="text-gold"
                    >
                      Registrati
                    </Link>
                  </li>
                </>
              )}
            </ul>
          </nav>
        )}
      </div>
    </header>
  )
}
